import { RequestApiPrivate } from "../../component/api/axios";
import type { ExportStatusResponse } from "./TicketExportApi";
import type {
  MissionStatusRatioDatum,
  MissionTrendDatum,
  ProcessStatusDatum,
  ProcessStatusSeries,
  MissionListRow,
  MissionListStatus,
  MissionStatus,
  MissionTrendComparePreviousMonth,
  MissionReportFilterValues
} from "../types/report.types";

async function postProcessReport<T>(
  path: string,
  body: Record<string, unknown> = {}
): Promise<T> {
  const { data } = await RequestApiPrivate.post(
    `process-reports/${path}`,
    body
  );
  return data.data;
}

const STATUS_TO_APPROVAL_CODES: Record<MissionStatus, number[]> = {
  inProgress: [1],
  completed: [2],
  cancelled: [3, 4]
};

const APPROVAL_CODE_TO_STATUS: Record<number, MissionStatus> = Object.entries(
  STATUS_TO_APPROVAL_CODES
).reduce(
  (acc, [status, codes]) => {
    codes.forEach((code) => {
      acc[code] = status as MissionStatus;
    });
    return acc;
  },
  {} as Record<number, MissionStatus>
);

export function filtersToParams(
  filters: Partial<MissionReportFilterValues> = {}
): Record<string, unknown> {
  const statusCodes = Array.from(
    new Set((filters.status ?? []).flatMap((s) => STATUS_TO_APPROVAL_CODES[s]))
  );
  // workspace_ids rỗng ("tất cả") → fallback sang divisionWorkspaceIds (toàn bộ node selectable của cây
  // Đơn vị dropdown, tính sẵn ở FE qua getAllSelectableWorkspaceIds trong ReportFilters.handleApply) cho
  // division_workspace_ids.
  const divisionWorkspaceIds =
    !filters.workspaceIds && filters.divisionWorkspaceIds?.length
      ? filters.divisionWorkspaceIds
      : undefined;
  return {
    start_date: filters.startDate,
    end_date: filters.endDate,
    workspace_ids: filters.workspaceIds,
    division_workspace_ids: divisionWorkspaceIds,
    assignee_ids: filters.assigneeIds,
    // Chỉ BE dùng để OR-mở rộng khi assignee_ids rỗng (chưa chọn tay ai) — xem
    // TaskReportRepositoryEloquent::applyAssigneeFilter.
    department_member_ids: filters.departmentMemberIds,
    // Đơn vị level 5 đang chọn đã roll-up lên phòng ban (level 4), FE tự tính — thay cho M_deptRollup
    // all-time cũ ở BE, dùng cho nhánh Qv mới của TaskReportRepositoryEloquent::applyAssigneeFilter.
    department_workspace_ids: filters.departmentWorkspaceIds,
    status: statusCodes,
    process_ids: filters.processIds
  };
}

// Label bị bỏ khỏi đây có chủ đích — legend cần hiển thị theo locale UI hiện tại (vn/en), nên FE
// (MissionReport) tự resolve label qua i18n dựa trên `key`, xem missionReport.charts.series.* trong
// src/locales/{vn,en}/report.translation.json.
export const missionProcessStatusSeriesMeta: Array<
  Omit<ProcessStatusSeries, "label">
> = [
  { key: "completedOnTime", color: "#29CC6A" },
  { key: "completedOverdue", color: "#F0B71C" },
  { key: "inProgress", color: "#248FED" },
  { key: "overdueUnprocessed", color: "#EF4949" }
];

const BUCKET_KEY_TO_DATUM_KEY: Record<string, string> = {
  processing: "inProgress",
  on_time_done: "completedOnTime",
  late_done: "completedOverdue",
  overdue: "overdueUnprocessed"
};

const BUCKET_KEY_TO_COLOR: Record<string, string> = {
  processing: "#248FED",
  on_time_done: "#29CC6A",
  late_done: "#F0B71C",
  overdue: "#EF4949"
};

interface TaskStatusSummaryBucket {
  key: string;
  count: number;
  percent: number;
}

interface TaskStatusSummaryResponse {
  total: number;
  buckets: TaskStatusSummaryBucket[];
}

export const getMissionStatusRatio = async (
  filters: Partial<MissionReportFilterValues> = {}
): Promise<MissionStatusRatioDatum[]> => {
  const { buckets } = await postProcessReport<TaskStatusSummaryResponse>(
    "task-status-summary",
    filtersToParams(filters)
  );
  return (buckets ?? []).map((bucket) => ({
    key: BUCKET_KEY_TO_DATUM_KEY[bucket.key] ?? bucket.key,
    value: bucket.count,
    percent: bucket.percent,
    color: BUCKET_KEY_TO_COLOR[bucket.key] ?? "#898781"
  }));
};

interface TaskTrendResponse {
  series: Array<{ month: string; total: number }>;
  compare_previous_month: {
    from: string;
    to: string;
    mode: "percent" | "count";
    delta_percent: number | null;
    delta_count: number | null;
    direction: "up" | "down" | "flat";
  } | null;
}

export interface MissionTrendResult {
  series: MissionTrendDatum[];
  comparePreviousMonth: MissionTrendComparePreviousMonth | null;
}

export const getMissionTrend = async (
  filters: Partial<MissionReportFilterValues> = {}
): Promise<MissionTrendResult> => {
  const { series, compare_previous_month } =
    await postProcessReport<TaskTrendResponse>(
      "task-trend",
      filtersToParams(filters)
    );
  return {
    series: (series ?? []).map((item) => ({
      month: item.month,
      total: item.total
    })),
    comparePreviousMonth: compare_previous_month
      ? {
          from: compare_previous_month.from,
          to: compare_previous_month.to,
          mode: compare_previous_month.mode,
          deltaPercent: compare_previous_month.delta_percent,
          deltaCount: compare_previous_month.delta_count,
          direction: compare_previous_month.direction
        }
      : null
  };
};

interface TaskStatusByProcessItem {
  process_id: number;
  process_name: string;
  total: number;
  buckets: Record<string, number>;
}

interface TaskStatusByProcessResponse {
  processes: TaskStatusByProcessItem[];
  total_process_matched: number;
}

export const getMissionStatusByProcess = async (
  top = 20,
  filters: Partial<MissionReportFilterValues> = {}
): Promise<ProcessStatusDatum[]> => {
  const { processes } = await postProcessReport<TaskStatusByProcessResponse>(
    "task-status-by-process",
    { top, ...filtersToParams(filters) }
  );
  return (processes ?? []).map((process) => {
    const datum: ProcessStatusDatum = { processName: process.process_name };
    Object.entries(process.buckets ?? {}).forEach(([bucketKey, count]) => {
      const datumKey = BUCKET_KEY_TO_DATUM_KEY[bucketKey] ?? bucketKey;
      datum[datumKey] = count;
    });
    return datum;
  });
};

interface TaskListItem {
  task_id: number;
  process_name: string;
  task_name: string;
  progress: { current: number; total: number };
  approval_status: number;
  is_overdue: boolean;
  is_late_complete: boolean;
  ticket_request_name: string;
  assigned_by_name: string | null;
  assignee_name: string | null;
  expected_complete_time: number | null;
  actual_complete_time: number | null;
}

interface TaskListResponse {
  list: TaskListItem[];
  page?: number;
  limit?: number;
  totalItem?: number;
}

function toPositiveInt(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.trunc(value)
    : fallback;
}

function mapTaskToRow(task: TaskListItem): MissionListRow {
  const status: MissionListStatus =
    APPROVAL_CODE_TO_STATUS[task.approval_status] ?? "unknown";
  return {
    id: task.task_id,
    processName: task.process_name ?? "-",
    missionName: task.task_name,
    progressDone: task.progress?.current ?? 0,
    progressTotal: task.progress?.total ?? 0,
    status,
    isOverdue: task.is_overdue || task.is_late_complete,
    requestCode: task.ticket_request_name ?? "-",
    assignee: task.assignee_name ?? "-",
    assignedBy: task.assigned_by_name ?? "-",
    requestedDueAt: task.expected_complete_time,
    actualCompletedAt: task.actual_complete_time
  };
}

export interface MissionListResult {
  rows: MissionListRow[];
  page: number;
  limit: number;
  totalItem: number;
}

export const getMissionList = async (
  page = 1,
  limit = 20,
  filters: Partial<MissionReportFilterValues> = {}
): Promise<MissionListResult> => {
  const response = await postProcessReport<TaskListResponse>("tasks", {
    page,
    limit,
    ...filtersToParams(filters)
  });
  const rows = (response.list ?? []).map(mapTaskToRow);
  const resolvedLimit = toPositiveInt(response.limit, limit);
  const resolvedPage =
    typeof response.page === "number" &&
    Number.isFinite(response.page) &&
    response.page >= page
      ? Math.trunc(response.page)
      : page;
  const hasFullPage = rows.length > 0 && rows.length >= resolvedLimit;
  const resolvedTotalItem =
    typeof response.totalItem === "number" &&
    Number.isFinite(response.totalItem)
      ? response.totalItem
      : (resolvedPage - 1) * resolvedLimit +
        rows.length +
        (hasFullPage ? 1 : 0);

  return {
    rows,
    page: resolvedPage,
    limit: resolvedLimit,
    totalItem: resolvedTotalItem
  };
};

export const createTaskExport = (
  filters: Partial<MissionReportFilterValues> = {}
): Promise<ExportStatusResponse> =>
  postProcessReport<ExportStatusResponse>(
    "task/export",
    filtersToParams(filters)
  );
